import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createActivity } from '../lib/supabase/activities';
import { Activity } from '../types/activity';
import { addNotification } from '@/lib/supabase/notifications';
import { supabase } from '@/lib/supabase';

export function useActivityCreation() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handleCreateActivity = async (activity: Omit<Activity, 'id'>) => {
    setIsSubmitting(true);
    setIsSuccess(false);

    try {
      /** 1️⃣  Authenticated user (Supabase Auth) */
      const {
        data: { user: authUser },
        error: authErr,
      } = await supabase.auth.getUser();
      if (authErr) throw authErr;
      if (!authUser) throw new Error('Not authenticated');

      /** 2️⃣  Sender’s full name + internal users.id */
      const { data: sender, error: senderErr } = await supabase
        .from('users')
        .select('id, full_name')
        .eq('auth_id', authUser.id)
        .single();
      if (senderErr) throw senderErr;
      if (!sender) throw new Error('User not found');

      /** 3️⃣  Persist the new activity  */
      const newActivity = await createActivity(activity);
      // newActivity.player_2_id is the FK-safe UUID we need

      /** 4️⃣  Notify the opponent (internal users.id!) */
      await addNotification({
        type: 'match_request',
        content: `${sender.full_name} invited you to play ${activity.sport}.`,
        user_id: newActivity.player_2_id, // ✅ FK-safe
        sender_id: sender.id,
        metadata: {
          activeTab: 'activities',
          activityId: newActivity.id,
        },
      });

      setIsSuccess(true);
      // navigate('/somewhere');  // optional redirect
    } catch (err) {
      console.error('Failed to create activity:', err);
      throw err; // bubble up to the form
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, isSuccess, handleCreateActivity };
}
